<template>
  <div>
    <div v-if="loading" class="text-center p-5">
      <b-spinner variant="info" />
    </div>
    <b-alert v-else-if="loadError" show variant="danger">
      {{ $t("label.roleLoadError") }}
      <b-button
        type="button"
        size="sm"
        variant="outline-danger"
        @click="loadRole"
        >{{ $t("label.retryPermissionDefinitions") }}</b-button
      >
    </b-alert>
    <form v-else @submit.prevent="submitForm">
      <b-form-group :label="$t('label.name')"
        ><b-form-input v-model.trim="form.name" required
      /></b-form-group>
      <b-form-group :label="$t('label.description')"
        ><b-form-textarea v-model.trim="form.description" rows="3"
      /></b-form-group>
      <b-form-checkbox v-model="form.isActive" switch>{{
        $t("label.active")
      }}</b-form-checkbox>
      <hr />
      <h6>{{ $t("label.roleDefaultPermissions") }}</h6>
      <div v-if="definitionsLoading" class="text-center p-3">
        <b-spinner small variant="info" />
      </div>
      <b-alert v-else-if="definitionsError" show variant="danger">
        {{ $t("label.permissionDefinitionsLoadError") }}
        <b-button
          type="button"
          size="sm"
          variant="outline-danger"
          @click="loadDefinitions"
          >{{ $t("label.retryPermissionDefinitions") }}</b-button
        >
      </b-alert>
      <div v-else class="row">
        <div
          v-for="module in permissionModules"
          :key="module.key"
          class="col-md-6"
        >
          <b-form-group :label="module.name">
            <multiselect
              v-model="form.permissions[module.key]"
              :options="module.options"
              track-by="value"
              label="text"
              :allow-empty="false"
            >
              <template #option="{ option }"
                ><div>{{ option.text }}</div>
                <small class="permission-description">{{
                  option.description
                }}</small></template
              >
            </multiselect>
            <b-form-text v-if="form.permissions[module.key]">{{
              form.permissions[module.key].description
            }}</b-form-text>
          </b-form-group>
        </div>
      </div>
      <hr />
      <div class="d-flex justify-content-end">
        <b-button
          type="button"
          variant="danger"
          class="mr-2"
          @click="$emit('done')"
          >{{ $t("label.cancel") }}</b-button
        >
        <b-button
          type="submit"
          variant="info"
          :disabled="!status.save || !permissionsReady"
        >
          <span v-if="status.save">{{
            $t(roleUUID ? "label.updateX" : "label.createX", [
              $t("module.userRole"),
            ])
          }}</span
          ><b-spinner v-else small />
        </b-button>
      </div>
    </form>
  </div>
</template>

<script>
import { mapState } from "vuex";
import Multiselect from "vue-multiselect";
import { roleMethods } from "@/state/api";
import { userService } from "@/services/api/user.service";
import {
  userPermissionDefinitionsModel,
  userPermissionSelections,
} from "@/models/api/userPermissionDefinitions.model";

export default {
  name: "UserRoleForm",
  components: { Multiselect },
  props: {
    roleUUID: { type: String, default: "" },
    searchCriteria: { type: Object, required: true },
  },
  data() {
    return {
      loading: Boolean(this.roleUUID),
      loadError: false,
      definitionsLoading: false,
      definitionsError: false,
      permissionModules: [],
      form: {
        uuid: "",
        name: "",
        description: "",
        isActive: true,
        permissions: {},
      },
    };
  },
  computed: {
    ...mapState("api/role", ["status", "details"]),
    permissionsReady() {
      return (
        !this.definitionsLoading &&
        !this.definitionsError &&
        this.permissionModules.length > 0 &&
        this.permissionModules.every((module) =>
          module.options.includes(this.form.permissions[module.key]),
        )
      );
    },
  },
  async created() {
    await this.loadDefinitions();
    if (this.roleUUID) await this.loadRole();
  },
  methods: {
    ...roleMethods,
    async loadDefinitions() {
      this.definitionsLoading = true;
      this.definitionsError = false;
      try {
        this.permissionModules = userPermissionDefinitionsModel(
          await userService.getPermissionDefinitions(),
        );
        const current = Object.fromEntries(
          Object.entries(this.form.permissions)
            .filter(([, option]) => option != null)
            .map(([key, option]) => [key, option.value]),
        );
        this.form.permissions = userPermissionSelections(
          this.permissionModules,
          {
            ...(this.roleUUID ? this.details?.permissions || {} : {}),
            ...current,
          },
        );
      } catch (error) {
        this.definitionsError = true;
      } finally {
        this.definitionsLoading = false;
      }
    },
    async loadRole() {
      this.loading = true;
      this.loadError = false;
      try {
        const result = await this.getRole({ uuid: this.roleUUID });
        if (!result?.success) {
          this.loadError = true;
          return;
        }
        this.form = {
          ...this.details,
          permissions: userPermissionSelections(
            this.permissionModules,
            this.details.permissions,
          ),
        };
      } catch (error) {
        this.loadError = true;
      } finally {
        this.loading = false;
      }
    },
    async submitForm() {
      if (!this.status.save || !this.permissionsReady || !this.form.name.trim())
        return;
      const permissions = Object.fromEntries(
        this.permissionModules.map((module) => [
          module.key,
          this.form.permissions[module.key].value,
        ]),
      );
      const action = this.roleUUID ? this.updateRole : this.createRole;
      const result = await action({
        data: { ...this.form, name: this.form.name.trim(), permissions },
        filter: this.searchCriteria,
      });
      
      if (result?.success) {
        this.$emit("done");
      }
    },
  },
};
</script>

<style scoped>
.permission-description {
  white-space: normal;
}
</style>
